export const DISPLAY_CURRENCIES = Object.freeze(['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'NZD', 'HKD', 'JPY', 'CHF', 'SGD', 'CNY']);
export const DISPLAY_CURRENCY_KEY = 'gfm-display-currency-v1';
export const DISPLAY_COUNTRY_KEY = 'gfm-display-country-v1';
export const DISPLAY_RATES_KEY = 'gfm-display-rates-v1';
export const REFERENCE_RATE_URL = 'https://api.frankfurter.dev/v2/rates?base=usd&quotes=eur,gbp,cad,aud,nzd,hkd,jpy,chf,sgd,cny&providers=ecb';

const CURRENCIES = new Set(DISPLAY_CURRENCIES);
const QUOTES = DISPLAY_CURRENCIES.filter((code) => code !== 'USD');
const DAY = 86_400_000;
const MONEY = '[data-gfm-usd-cents]';
const SELECT = '[data-gfm-display-currency]';
const COUNTRY = '[data-gfm-country]';
const COUNTRY_CURRENCIES = Object.freeze({ CA: 'CAD', GB: 'GBP', AU: 'AUD' });
const LABEL = '[data-gfm-currency-label]';
const STATUS = '[data-gfm-currency-status]';
const CHECKOUT_NOTE = '[data-gfm-checkout-currency-note]';
const UI = `${SELECT},${LABEL},${STATUS},${CHECKOUT_NOTE}`;

export function validateReferenceRates(rows, now = Date.now()) {
  if (!Array.isArray(rows) || rows.length !== QUOTES.length) throw new Error('Invalid exchange-rate rows');
  const rates = { USD: 1 };
  let asOf;
  for (const row of rows) {
    if (!row || typeof row !== 'object' || row.base !== 'USD' || !QUOTES.includes(row.quote)) throw new Error('Invalid exchange-rate currency');
    if (Object.hasOwn(rates, row.quote) || typeof row.rate !== 'number' || !Number.isFinite(row.rate) || row.rate <= 0) throw new Error('Invalid exchange rate');
    if (typeof row.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(row.date)) throw new Error('Invalid exchange-rate date');
    const date = Date.parse(`${row.date}T00:00:00Z`);
    if (!Number.isFinite(date) || new Date(date).toISOString().slice(0, 10) !== row.date) throw new Error('Invalid exchange-rate date');
    const age = Math.floor(now / DAY) - Math.floor(date / DAY);
    if (age < 0 || age > 7 || (asOf && asOf !== row.date)) throw new Error('Exchange-rate date is unavailable');
    asOf = row.date;
    rates[row.quote] = row.rate;
  }
  if (QUOTES.some((quote) => !Object.hasOwn(rates, quote))) throw new Error('Missing exchange-rate currency');
  return { rates, asOf };
}

export function validateRateCache(cache, now = Date.now()) {
  if (!cache || cache.version !== 1 || cache.provider !== 'ecb' || cache.base !== 'USD') throw new Error('Invalid exchange-rate cache');
  if (!Number.isSafeInteger(cache.fetchedAt) || cache.fetchedAt > now || now - cache.fetchedAt >= DAY) throw new Error('Exchange-rate cache expired');
  return { ...validateReferenceRates(cache.rows, now), fetchedAt: cache.fetchedAt };
}

export function formatReferencePrice(cents, currency, rates, locale = 'en') {
  if (!Number.isSafeInteger(cents) || !CURRENCIES.has(currency) || !Number.isFinite(rates[currency]) || rates[currency] <= 0) throw new Error('Invalid reference price');
  const amount = (cents / 100) * rates[currency];
  if (!Number.isFinite(amount)) throw new Error('Reference price exceeds supported range');
  const options = { style: 'currency', currency, currencyDisplay: 'code' };
  let formatter;
  try {
    formatter = new Intl.NumberFormat(locale, options);
  } catch {
    formatter = new Intl.NumberFormat('en', options);
  }
  return formatter.format(amount);
}

export class DisplayCurrencyController {
  constructor(document, environment = {}) {
    this.document = document;
    this.window = environment.window ?? document.defaultView ?? globalThis;
    this.fetch = environment.fetch ?? this.window.fetch?.bind(this.window);
    this.now = environment.now ?? (() => Date.now());
    this.setTimer = environment.setTimeout ?? this.window.setTimeout.bind(this.window);
    this.clearTimer = environment.clearTimeout ?? this.window.clearTimeout.bind(this.window);
    this.enqueue = environment.queueMicrotask ?? this.window.queueMicrotask.bind(this.window);
    this.Observer = environment.MutationObserver ?? this.window.MutationObserver;
    this.AbortController = environment.AbortController ?? this.window.AbortController;
    try {
      this.storage = environment.storage ?? this.window.localStorage;
    } catch {
      this.storage = undefined;
    }
    this.currency = 'USD';
    this.requestedCurrency = 'USD';
    this.loading = false;
    this.error = false;
    this.nativeCurrency = 'USD';
    this.rates = undefined;
    this.cache = undefined;
    this.requestSequence = 0;
    this.records = new WeakMap();
    this.pendingNodes = new Set();
    this.pendingUI = false;
    this.queued = false;
    this.destroyed = false;
    this.onChange = (event) => {
      const select = event.target;
      if (select?.matches?.(SELECT)) void this.selectCurrency(select.value);
    };
    this.onStorage = (event) => {
      if (event.key !== DISPLAY_CURRENCY_KEY) return;
      if (event.newValue !== this.readStorage(DISPLAY_CURRENCY_KEY)) return;
      const savedCountry = this.readStorage(DISPLAY_COUNTRY_KEY);
      if (!this.country || !savedCountry || savedCountry === this.country) void this.selectCurrency(event.newValue, false);
    };
    this.onPageShow = () => {
      const currency = this.nativeCurrency === 'USD' ? this.restoreCountryCurrency() : 'USD';
      if (this.nativeCurrency !== 'USD' || (this.loading && currency === this.requestedCurrency)) this.reconcile(this.document, true);
      else void this.selectCurrency(currency, false);
    };
    this.onVisibility = () => {
      if (!this.document.hidden && this.currency !== 'USD' && !this.usableRates()) void this.selectCurrency(this.currency, false);
    };
  }

  initialize() {
    const firstSelect = this.document.querySelector(SELECT);
    const firstMoney = this.document.querySelector(MONEY);
    this.nativeCurrency = firstSelect?.dataset.gfmBaseCurrency ?? firstMoney?.dataset.gfmBaseCurrency ?? 'USD';
    this.reconcile(this.document, true);
    this.document.addEventListener('change', this.onChange);
    this.document.addEventListener('visibilitychange', this.onVisibility);
    this.window.addEventListener?.('storage', this.onStorage);
    this.window.addEventListener?.('pageshow', this.onPageShow);
    if (this.Observer && this.document.body) {
      this.observer = new this.Observer((mutations) => this.onMutations(mutations));
      this.observer.observe(this.document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['data-gfm-usd-cents', 'data-gfm-usd-original', 'data-gfm-base-currency'],
      });
    }
    if (this.nativeCurrency === 'USD') void this.selectCurrency(this.restoreCountryCurrency(), false);
    return this;
  }

  restoreCountryCurrency() {
    const value = this.document.querySelector(COUNTRY)?.dataset.gfmCountry;
    const country = typeof value === 'string' && /^[A-Z]{2}$/.test(value) ? value : undefined;
    const savedCountry = this.readStorage(DISPLAY_COUNTRY_KEY);
    const previousCountry = this.country ?? savedCountry;
    const saved = this.readStorage(DISPLAY_CURRENCY_KEY);
    let currency = CURRENCIES.has(saved) ? saved : this.requestedCurrency;
    if (country && this.country === country) currency = this.requestedCurrency;
    else if (country && previousCountry !== country) currency = COUNTRY_CURRENCIES[country] ?? 'USD';
    this.country = country;
    if (country && (previousCountry !== country || savedCountry !== country)) this.writeCurrencyPreference(currency);
    return currency;
  }

  readStorage(key) {
    try { return this.storage?.getItem(key); } catch { return undefined; }
  }

  writeStorage(key, value) {
    try { this.storage?.setItem(key, value); } catch {}
  }

  writeCurrencyPreference(currency) {
    this.writeStorage(DISPLAY_CURRENCY_KEY, currency);
    if (this.country) this.writeStorage(DISPLAY_COUNTRY_KEY, this.country);
  }

  usableRates() {
    if (!this.cache) return false;
    try {
      this.rates = validateRateCache(this.cache, this.now());
      return true;
    } catch { return false; }
  }

  async ensureRates() {
    if (this.usableRates()) return this.rates;
    try {
      const cached = JSON.parse(this.readStorage(DISPLAY_RATES_KEY) ?? 'null');
      this.rates = validateRateCache(cached, this.now());
      this.cache = cached;
      return this.rates;
    } catch {}
    if (this.ratesPromise) return this.ratesPromise;
    if (!this.fetch || !this.AbortController) throw new Error('Exchange-rate service unavailable');
    const controller = new this.AbortController();
    this.fetchController = controller;
    let timeout;
    const deadline = new Promise((_, reject) => {
      timeout = this.setTimer(() => {
        controller.abort();
        reject(new Error('Exchange-rate request timed out'));
      }, 5000);
    });
    const request = (async () => {
      const response = await this.fetch(REFERENCE_RATE_URL, {
        method: 'GET', mode: 'cors', credentials: 'omit', cache: 'no-cache', referrerPolicy: 'no-referrer', signal: controller.signal,
      });
      if (!response.ok) throw new Error('Exchange-rate service unavailable');
      const rows = await response.json();
      const validated = validateReferenceRates(rows, this.now());
      const cache = { version: 1, provider: 'ecb', base: 'USD', fetchedAt: this.now(), rows };
      if (!controller.signal.aborted) {
        this.cache = cache;
        this.rates = { ...validated, fetchedAt: cache.fetchedAt };
        this.writeStorage(DISPLAY_RATES_KEY, JSON.stringify(cache));
      }
      return { ...validated, fetchedAt: cache.fetchedAt };
    })();
    const pending = Promise.race([request, deadline]);
    this.ratesPromise = pending;
    try { return await pending; }
    finally {
      this.clearTimer(timeout);
      if (this.ratesPromise === pending) this.ratesPromise = undefined;
      if (this.fetchController === controller) this.fetchController = undefined;
    }
  }

  async selectCurrency(value, persist = true) {
    if (this.destroyed) return;
    const currency = CURRENCIES.has(value) ? value : 'USD';
    const sequence = ++this.requestSequence;
    this.clearTimer(this.refreshTimer);
    this.requestedCurrency = currency;
    this.error = false;
    if (this.nativeCurrency !== 'USD') {
      this.fallbackUSD();
      return;
    }
    if (currency === 'USD') {
      this.fetchController?.abort();
      this.ratesPromise = undefined;
      this.loading = false;
      this.currency = 'USD';
      if (persist) this.writeCurrencyPreference('USD');
      this.reconcile(this.document, true);
      return;
    }
    this.loading = true;
    this.currency = 'USD';
    this.reconcile(this.document, true);
    try {
      const rates = await this.ensureRates();
      if (this.destroyed || sequence !== this.requestSequence) return;
      this.rates = rates;
      this.loading = false;
      this.currency = currency;
      if (!this.reconcile(this.document, true)) return;
      if (persist) this.writeCurrencyPreference(currency);
      this.scheduleRefresh();
    } catch {
      if (!this.destroyed && sequence === this.requestSequence) this.fallbackUSD();
    }
  }

  fallbackUSD() {
    ++this.requestSequence;
    this.fetchController?.abort();
    this.ratesPromise = undefined;
    this.clearTimer(this.refreshTimer);
    this.currency = 'USD';
    this.requestedCurrency = 'USD';
    this.loading = false;
    this.error = true;
    this.writeCurrencyPreference('USD');
    this.reconcile(this.document, true);
  }

  scheduleRefresh() {
    if (!this.rates || this.currency === 'USD') return;
    const sourceExpiry = Date.parse(`${this.rates.asOf}T00:00:00Z`) + 8 * DAY;
    const expiresAt = Math.min(this.rates.fetchedAt + DAY, sourceExpiry);
    this.refreshTimer = this.setTimer(() => {
      if (!this.destroyed && this.currency !== 'USD') void this.selectCurrency(this.currency, false);
    }, Math.max(1, expiresAt - this.now()));
  }

  moneyNodes(root) {
    const nodes = Array.from(root.querySelectorAll?.(MONEY) ?? []);
    if (root.matches?.(MONEY)) nodes.unshift(root);
    return nodes;
  }

  originalPrice(element) {
    const original = element.getAttribute('data-gfm-usd-original');
    const key = `${element.dataset.gfmUsdCents}|${element.dataset.gfmBaseCurrency}|${original}`;
    let record = this.records.get(element);
    if (!record || record.key !== key) {
      let html = original ?? element.innerHTML;
      if (original !== null && this.document.createElement) {
        const template = this.document.createElement('template');
        template.innerHTML = original;
        html = template.innerHTML;
      }
      record = { key, html };
      this.records.set(element, record);
    }
    return record.html;
  }

  reconcile(root = this.document, syncUI = false) {
    if (this.destroyed) return false;
    if (this.currency !== 'USD' && !this.usableRates()) {
      void this.selectCurrency(this.currency, false);
      return false;
    }
    const plans = [];
    try {
      for (const element of this.moneyNodes(root)) {
        const original = this.originalPrice(element);
        if (this.currency === 'USD') {
          plans.push({ element, html: original });
          continue;
        }
        if ((element.dataset.gfmBaseCurrency ?? this.nativeCurrency) !== 'USD') {
          this.nativeCurrency = element.dataset.gfmBaseCurrency || this.nativeCurrency;
          throw new Error('Different native currency');
        }
        if (!/^-?\d+$/.test(element.dataset.gfmUsdCents ?? '')) throw new Error('Invalid native amount');
        const cents = Number(element.dataset.gfmUsdCents);
        const locale = this.document.documentElement?.lang || 'en';
        plans.push({ element, text: formatReferencePrice(cents, this.currency, this.rates.rates, locale) });
      }
    } catch {
      this.fallbackUSD();
      return false;
    }
    for (const { element, html, text } of plans) {
      if (text !== undefined) {
        if (element.textContent !== text) element.textContent = text;
      } else if (element.innerHTML !== html) element.innerHTML = html;
    }
    if (syncUI) this.syncUI();
    return true;
  }

  syncUI() {
    const enabled = this.nativeCurrency === 'USD';
    for (const select of this.document.querySelectorAll(SELECT)) {
      select.disabled = !enabled;
      select.value = this.loading ? this.requestedCurrency : this.currency;
      select.setAttribute('aria-busy', this.loading ? 'true' : 'false');
    }
    for (const label of this.document.querySelectorAll(LABEL)) {
      const value = enabled ? this.currency : this.nativeCurrency;
      if (label.textContent !== value) label.textContent = value;
    }
    for (const note of this.document.querySelectorAll(CHECKOUT_NOTE)) note.hidden = !enabled;
    for (const status of this.document.querySelectorAll(STATUS)) {
      const reminder = status.hasAttribute('data-gfm-currency-reminder');
      const key = this.error ? 'gfmUnavailableText' : this.loading ? 'gfmLoadingText' : 'gfmReadyText';
      const alternativeKey = this.error ? 'unavailableText' : this.loading ? 'loadingText' : 'readyText';
      const fallback = !enabled ? 'Display conversion is unavailable for this checkout currency.' : this.error ? 'Currency conversion is unavailable. Prices and checkout remain in USD.' : this.loading ? 'Loading exchange rates. Prices are shown in USD; checkout is in USD.' : this.currency !== 'USD' ? 'Approximate __CURRENCY__ prices. Checkout is charged in USD.' : 'Prices and checkout are in USD.';
      const message = (!enabled ? fallback : status.dataset[key] ?? status.dataset[alternativeKey] ?? fallback)
        .replaceAll('__CURRENCY__', this.currency).replaceAll('__DATE__', this.rates?.asOf ?? '');
      const normalUSD = enabled && !this.error && !this.loading && this.currency === 'USD';
      const compactReady = status.hasAttribute('data-gfm-currency-compact') && enabled && !this.error && !this.loading;
      const hideStatus = normalUSD || compactReady;
      status.hidden = hideStatus;
      const visibleMessage = hideStatus ? '' : message;
      if (status.textContent !== visibleMessage) status.textContent = visibleMessage;
    }
  }

  onMutations(mutations) {
    for (const mutation of mutations) {
      const target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
      const money = target?.closest?.(MONEY);
      if (money) this.pendingNodes.add(money);
      if (target?.matches?.(UI)) this.pendingUI = true;
      for (const node of mutation.addedNodes ?? []) {
        if (node.nodeType !== 1) continue;
        for (const price of this.moneyNodes(node)) this.pendingNodes.add(price);
        if (node.matches?.(UI) || node.querySelector?.(UI)) this.pendingUI = true;
      }
    }
    if (this.queued || (!this.pendingNodes.size && !this.pendingUI)) return;
    this.queued = true;
    this.enqueue(() => {
      this.queued = false;
      if (this.destroyed) return;
      const prices = Array.from(this.pendingNodes);
      const syncUI = this.pendingUI;
      this.pendingNodes.clear();
      this.pendingUI = false;
      for (const price of prices) if (price.isConnected !== false) this.reconcile(price);
      if (syncUI) this.syncUI();
    });
  }

  destroy() {
    this.destroyed = true;
    ++this.requestSequence;
    this.fetchController?.abort();
    this.observer?.disconnect();
    this.clearTimer(this.refreshTimer);
    this.document.removeEventListener('change', this.onChange);
    this.document.removeEventListener('visibilitychange', this.onVisibility);
    this.window.removeEventListener?.('storage', this.onStorage);
    this.window.removeEventListener?.('pageshow', this.onPageShow);
  }
}

if (typeof document !== 'undefined') {
  const initialize = () => {
    if (!window.__gfmDisplayCurrencyV1) window.__gfmDisplayCurrencyV1 = new DisplayCurrencyController(document).initialize();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
}
